import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import api from '../../../services/api';
import { MINING_TZ } from '../../../utils/dateUtils';

const PAGE_LIMIT = 500;

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: MINING_TZ,
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: MINING_TZ,
  });
};

const formatWeight = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(n)) return String(value);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const RegNoTransactionsModalAmendments = ({
  isOpen,
  onClose,
  regNo,
  clickedTicketNo,
  inheritedFilters,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState([]);

  // Inherit the page's scope filters (date range, locations, haulier, etc.) but
  // drop anything that identifies a single row — otherwise the query narrows to
  // just the clicked ticket instead of every transaction for the registration.
  const effectiveFilters = useMemo(() => {
    const base = inheritedFilters && typeof inheritedFilters === 'object' ? inheritedFilters : {};
    const {
      ticketNo,
      deliveryNo,
      orderNo,
      refNo,
      ticketUIDs,
      registration: _ignored,
      ...scopeFilters
    } = base;
    return { ...scopeFilters, registration: regNo };
  }, [inheritedFilters, regNo]);

  const fetchTransactions = useCallback(async () => {
    if (!regNo) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.amendmentsV3.getLatestTickets({
        ...effectiveFilters,
        page: 1,
        limit: PAGE_LIMIT,
        sortBy: '[Ticket Timestamp]',
        sortDir: 'ASC',
      });
      setTickets(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching transactions by Reg No:', err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load transactions';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [regNo, effectiveFilters]);

  useEffect(() => {
    if (isOpen && regNo) {
      fetchTransactions();
    } else if (!isOpen) {
      setTickets([]);
      setError(null);
    }
  }, [isOpen, regNo, fetchTransactions]);

  // Build a human-readable scope summary (date range + other filter count)
  const scope = useMemo(() => {
    const f = inheritedFilters || {};
    const dr = f.dateRange;
    let dateLabel = null;
    if (dr && (dr.startDate || dr.endDate)) {
      const fmt = (s) => {
        if (!s) return '—';
        const d = new Date(s);
        if (isNaN(d.getTime())) return String(s);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      };
      if (dr.startDate && dr.endDate) {
        dateLabel =
          dr.startDate === dr.endDate
            ? fmt(dr.startDate)
            : `${fmt(dr.startDate)} → ${fmt(dr.endDate)}`;
      } else {
        dateLabel = `from ${fmt(dr.startDate || dr.endDate)}`;
      }
    }

    const DROPPED_KEYS = new Set([
      'dateRange',
      'registration',
      'ticketNo',
      'deliveryNo',
      'orderNo',
      'refNo',
      'ticketUIDs',
    ]);
    const otherActive = Object.entries(f).filter(([key, value]) => {
      if (DROPPED_KEYS.has(key)) return false;
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
        return false;
      return true;
    });

    return { dateLabel, otherCount: otherActive.length };
  }, [inheritedFilters]);

  const totals = useMemo(() => {
    const totalNett = tickets.reduce((sum, t) => {
      const n = parseFloat(t['Nett Weight']);
      return isNaN(n) ? sum : sum + n;
    }, 0);
    const typeCounts = tickets.reduce((acc, t) => {
      const type = t['Ticket Type'] || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    return { totalNett, typeCounts };
  }, [tickets]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-surface rounded-xl shadow-2xl ring-1 ring-gray-900/5 w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-7 py-5 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h3 className="font-display text-lg font-semibold tracking-tight text-gray-900 leading-tight">
                Transactions for
              </h3>
              <span className="font-mono text-lg font-semibold text-primary-700 tracking-tight">
                {regNo || '—'}
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs">
              <span className="uppercase tracking-[0.1em] text-gray-400 text-[10px] font-medium">
                Scope
              </span>
              {scope.dateLabel ? (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 ring-1 ring-inset ring-primary-200 px-2 py-0.5 text-[11px] font-medium text-primary-700 tabular-nums">
                  {scope.dateLabel}
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md bg-gray-100 ring-1 ring-inset ring-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                  All dates
                </span>
              )}
              {scope.otherCount > 0 && (
                <span className="inline-flex items-center rounded-md bg-gray-100 ring-1 ring-inset ring-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600 tabular-nums">
                  +{scope.otherCount} filter{scope.otherCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 -mr-1.5 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Summary strip */}
        {!loading && !error && tickets.length > 0 && (
          <div className="px-7 py-3 border-b border-gray-100 bg-gray-50/60 flex items-center gap-6 text-xs">
            <div className="flex items-baseline gap-1.5">
              <span className="uppercase tracking-[0.1em] text-gray-400 text-[10px] font-medium">
                Tickets
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">{tickets.length}</span>
            </div>
            <div className="h-4 w-px bg-gray-200" aria-hidden="true" />
            <div className="flex items-baseline gap-1.5">
              <span className="uppercase tracking-[0.1em] text-gray-400 text-[10px] font-medium">
                Nett total
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">
                {formatWeight(totals.totalNett)}
              </span>
            </div>
            {Object.entries(totals.typeCounts).map(([type, count]) => (
              <React.Fragment key={type}>
                <div className="h-4 w-px bg-gray-200" aria-hidden="true" />
                <div className="flex items-baseline gap-1.5">
                  <span className="uppercase tracking-[0.1em] text-gray-400 text-[10px] font-medium">
                    {type}
                  </span>
                  <span className="font-semibold text-gray-900 tabular-nums">{count}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <FaSpinner className="animate-spin w-4 h-4 text-primary-500" />
                <span>Loading transactions…</span>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center h-64 p-6">
              <div className="text-center max-w-sm">
                <p className="text-sm text-red-600 mb-3">{error}</p>
                <button
                  onClick={fetchTransactions}
                  className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && tickets.length === 0 && (
            <div className="flex items-center justify-center h-64 p-6">
              <p className="text-sm text-gray-500">
                No transactions found for this registration number.
              </p>
            </div>
          )}

          {!loading && !error && tickets.length > 0 && (
            <table className="w-full border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider w-10 text-center">
                    #
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Date
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Timestamp
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Type
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Ticket No
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Delivery No
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Dispatch Loc
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Receipt Loc
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Nett Wgt
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Product
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tickets.map((t, index) => {
                  const ticketNo = t['Ticket No'];
                  const isHighlighted = clickedTicketNo && ticketNo === clickedTicketNo;
                  const isReceipt = t['Ticket Type'] === 'Receipt';

                  let rowClass;
                  if (isHighlighted) {
                    rowClass = 'bg-amber-50 ring-2 ring-inset ring-amber-300';
                  } else if (isReceipt) {
                    rowClass = 'bg-emerald-50/40 hover:bg-emerald-50';
                  } else {
                    rowClass =
                      index % 2 === 0
                        ? 'bg-surface hover:bg-slate-50'
                        : 'bg-slate-50/40 hover:bg-slate-50';
                  }

                  return (
                    <tr
                      key={t.TicketUID || `${ticketNo}-${index}`}
                      className={`${rowClass} transition-colors`}
                    >
                      <td className="px-3 py-1.5 text-center text-slate-500 tabular-nums font-medium">
                        {index + 1}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-800 tabular-nums">
                        {formatDate(t['Ticket Timestamp'])}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-600 tabular-nums">
                        {formatDateTime(t['Ticket Timestamp'])}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                            isReceipt
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-sky-100 text-sky-700'
                          }`}
                        >
                          {t['Ticket Type'] || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap font-mono text-[11px] text-slate-900">
                        {ticketNo || '—'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap font-mono text-[11px] text-slate-600">
                        {t['Delivery No'] || '—'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                        {t['Dispatch Location'] || '—'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                        {t['Receipt Location'] || '—'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-slate-900 tabular-nums">
                        {formatWeight(t['Nett Weight'])}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                        {t['Product'] || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!loading && !error && tickets.length === PAGE_LIMIT && (
            <div className="px-7 py-2.5 text-[11px] text-gray-500 bg-amber-50/40 border-t border-amber-100">
              Showing the first {PAGE_LIMIT} transactions. Narrow your view with filters to see
              more.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-gray-200 bg-gray-50/80 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-md bg-surface px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegNoTransactionsModalAmendments;
